/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

// tslint:disable:max-func-body-length
import {
    ICheckoutViewProps,
    ILineItem,
    ILineItemDeliveryGroup,
    ILineItems,
    IOrderSummary,
    IPickUpAtStore
} from '@msdyn365-commerce-modules/checkout/src/modules/checkout/./checkout';

import { Alert, Module, Node } from '@msdyn365-commerce-modules/utilities';
import { IActionContext } from '@msdyn365-commerce/core';
import { CartLine, ProductSearchCriteria, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import get from 'lodash/get';
import React, { useEffect, useState } from 'react';
import { ICheckoutModulesSharedState } from '../../../actions/checkout-modules-state-share';
import { getSearchByCriteriaAsync, GetSearchByCriteriaInput } from '../../dobbies/actions/search-by-criteria';
import { ICheckoutProps } from '../definition-extensions/checkout.ext.props.autogenerated';
import { getRestrictedProducts } from '../utilities/post-code-restriction/get-restricted-products';
import { ICheckoutData } from './checkout.data';
import GuidedFormComponent, { ICheckoutGuidedFormProps } from './components/checkout/guided-form';
/* VSI Customization - START - 19/04/21
 * Following interface is used to pass product related info to LineItem component
 * to display error message for post code restriction */
interface IProductsDeliveryDetails {
    postCodeValidation?: ICheckoutModulesSharedState;
    cartLines?: CartLine[];
    plantProductsIds?: number[];
    postCodeErrorMsg?: string;
}
interface IProductDeliveryInfo {
    plantPostCodeInfo?: IProductsDeliveryDetails;
}
/* VSI Customization - END */

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const LineItemComponent: React.FC<ILineItem & IProductsDeliveryDetails> = ({
    LineItem,
    item,
    LineId,
    pickUpAtStore,
    cartLines,
    plantProductsIds,
    postCodeErrorMsg,
    postCodeValidation
}) => {
    /* VSI Customization - START - 19/04/21 - Display error msg against cartlines incase of post code retriction error */
    let postCodeError = <></>;

    if (!pickUpAtStore && (postCodeValidation?.invalidAddressForCartProducts || postCodeValidation?.notBtPostCode)) {
        // First find this cartLine Id in cartLines to get its product Id
        const cartLine = cartLines?.find(cartLineItem => {
            return cartLineItem.LineId === LineId;
        });
        const cartLineProductId = cartLine?.ProductId;
        // Now check if it is plant then show the message
        const isPlantProduct = plantProductsIds?.find(productId => productId === cartLineProductId);
        postCodeError = isPlantProduct ? (
            <Alert isOpen={!!(postCodeErrorMsg && postCodeErrorMsg.length > 0)} color='danger' assertive={true}>
                {postCodeErrorMsg}
            </Alert>
        ) : (
            postCodeError
        );
    }
    /* VSI Customization - END */
    return (
        <>
            <Node {...LineItem}>
                {item}
                <Alert isOpen={true} color='danger' assertive={true}>
                    {postCodeError}
                </Alert>
                {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
            </Node>
        </>
    );
};

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroup & IProductsDeliveryDetails> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    cartLines,
    plantProductsIds,
    postCodeValidation,
    postCodeErrorMsg
}) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponent
                    key={lineItem.LineId}
                    {...lineItem}
                    cartLines={cartLines}
                    plantProductsIds={plantProductsIds}
                    postCodeValidation={postCodeValidation}
                    postCodeErrorMsg={postCodeErrorMsg}
                />
            ))}
        </Node>
    </Node>
);

export const LineItemsComponent: React.FC<ILineItems & IProductDeliveryInfo> = ({
    LineItems,
    Header,
    heading,
    editLink,
    itemsForPickup,
    itemsForShip,
    plantPostCodeInfo
}) => (
    <Node {...LineItems}>
        <Node {...Header}>
            {heading}
            {editLink}
        </Node>
        {itemsForPickup && <LineItemGroupComponent {...itemsForPickup} />}
        {itemsForShip && <LineItemGroupComponent {...itemsForShip} {...plantPostCodeInfo} />}
    </Node>
);

//Customization removed because its now available out of the box in .29 version
// // const OrderSummaryComponent: React.FC<IOrderSummary> = ({ heading, lines}, {_checkoutCart}) => (
// export const _renderOrderSummarylines = (orderSummary: IOrderSummary, props: ICheckoutProps<{}>): JSX.Element | null => {
//     // let a = _cart;
//     // const taxAmount = props.cart && props.cart.cart.TaxAmount || 0;\
//     // @ts-ignore
//     const shippingAmount = props.data && props.data.checkout.result._checkoutCart._cart.ShippingChargeAmount;
//     // @ts-ignore
//     const subtotalAmount = props.data && props.data.checkout.result._checkoutCart._cart.SubtotalAmount;
//     // @ts-ignore
//     const subtotalSalesAmount = props.data && props.data.checkout.result._checkoutCart._cart.SubtotalSalesAmount;

//     const modifiedSubtoalAmount: number = Number(shippingAmount) + (Number(subtotalAmount) - subtotalSalesAmount);

//     /* VSI Customization - START*/
//     // @ts-ignore
//     const shippingDefaultText = props.data.checkout.result._checkoutCart._cart.DeliveryMode !== '' ? '£0.00' : 'To Be calculated';
//     const shippingText = shippingAmount > 0 ? `£${modifiedSubtoalAmount.toFixed(2)}` : shippingDefaultText;
//     /* VSI Customization - END*/

//     return (
//         <div className='msc-order-summary-wrapper'>
//             {orderSummary.heading}
//             <div className='msc-order-summary__items'>
//                 {orderSummary.lines && (
//                     /* VSI Customization - START*/
//                     // TODO: need to fix harcoded values
//                     // Customization Reason: Subtotal amount is adding shipping charges
//                     <>
//                         <p className='msc-order-summary__line-sub-total'>
//                             <span className='msc-order-summary__label'>{'Sub Total'}</span>
//                             <span className='msc-price msc-order-summary__value'>
//                                 <span className='msc-price__actual'>{`£${subtotalSalesAmount.toFixed(2)}`}</span>
//                             </span>
//                         </p>
//                         <p className='msc-order-summary__line-shipping'>
//                             <span className='msc-order-summary__label'>{'Home Delivery'}</span>
//                             <span className='msc-price msc-order-summary__value'>
//                                 <span className='msc-price__actual'>{shippingText}</span>
//                             </span>
//                         </p>
//                         {orderSummary.lines.otherCharge}
//                         {orderSummary.lines.loyalty}
//                         {orderSummary.lines.giftCard}
//                         {orderSummary.lines.orderTotal}
//                     </>
//                     /* VSI Customization - END */
//                 )}
//             </div>
//         </div>
//     );
// };
/* VSI Customization - START - 18/11/20 */
const getSlotItems = (key: string, props: ICheckoutViewProps): React.ReactNode[] => {
    const { slots } = props;
    return slots && slots[key] && slots[key].length ? slots[key] : [];
};

const getId = (item: React.ReactNode): string => {
    return get(item, 'props.id') || '';
};

// Following function gets product attributes and set in state
const _getProductAttributes = async (
    ctx: IActionContext,
    products: SimpleProduct[],
    setPlantProducts: React.Dispatch<React.SetStateAction<number[] | undefined>>
) => {
    // Filter plants products and send it to LineItems Component to display error msg against it incase postcode is not valid
    const productIds: number[] = [];
    products.map(product => productIds.push(product.RecordId));

    const callerContext = ctx;
    const {
        requestContext: {
            apiSettings: { channelId, catalogId }
        }
    } = ctx;
    const searchCriteriaInput: ProductSearchCriteria = {};
    searchCriteriaInput.Context = { ChannelId: channelId, CatalogId: catalogId };
    searchCriteriaInput.Ids = productIds;
    searchCriteriaInput.IncludeAttributes = true;

    const actionInput: GetSearchByCriteriaInput = {
        productIds: productIds,
        context: callerContext,
        pageType: 'checkout',
        dataCacheType: () => 'application' || 'request',
        getCacheKey: () => 'postCodeRestriction',
        getCacheObjectType: () => 'postCodeRestriction'
    };
    const productsSearchResult = await getSearchByCriteriaAsync(actionInput, callerContext);

    const plantsProducts: number[] = await getRestrictedProducts(productsSearchResult, ctx.requestContext.apiSettings.baseUrl);

    // Store plant product RecordIds in state
    setPlantProducts(plantsProducts);
};
/* VSI Customization - END */
const OrderSummaryComponent: React.FC<IOrderSummary> = ({ heading, lines }) => (
    <div className='msc-order-summary-wrapper'>
        {heading}
        <div className='msc-order-summary__items'>
            {lines && (
                <>
                    {lines.subtotal}
                    {lines.shipping}
                    {lines.otherCharge}
                    {lines.tax}
                    {lines.loyalty}
                    {lines.giftCard}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);
const CheckoutView: React.FC<ICheckoutViewProps & ICheckoutProps<ICheckoutData> & ICheckoutGuidedFormProps> = props => {
    const {
        config: { breadCrumbsRoot, breadCrumbs, postCodeErrorMsgForShippingAddress, postCodeErrorMsgForCartLines },
        context: { actionContext },
        canShow,
        checkoutProps,
        headerProps,
        hasSalesOrder,
        bodyProps,
        mainProps,
        sideProps,
        orderConfirmation,
        loading,
        alert,
        title,
        orderSummary,
        lineItems,
        resources,
        placeOrderButton,
        data: {
            checkoutModulesSharedState: { result: checkoutModulesSharedState }
        }
    } = props;

    const checkout = props.data.checkout?.result;
    /* VSI Customization - START - 18/11/20 */
    const [plantProducts, setPlantProducts] = useState<number[] | undefined>();
    useEffect(() => {
        if (!plantProducts) {
            props.data.products.then(productss => {
                _getProductAttributes(actionContext, productss, setPlantProducts).catch(error => props.telemetry.trace(error));
            });
        }
    });
    const items = getSlotItems('checkoutInformation', props);
    let creditBannerFound = false;
    let creditBanner: React.ReactNode = <></>;
    const checkoutItems = items;
    // store text block in credit-banner
    items &&
        items.map(item => {
            if (creditBannerFound === false) {
                const childId = getId(item);
                if (childId.includes('text-block')) {
                    creditBanner = item;
                    creditBannerFound = true;
                }
            }
        });
    const creditBannerSection = creditBanner && React.cloneElement(creditBanner as React.ReactElement);

    // Customization for Post code restriction to display msg against cartLine
    const cartLines = checkout && checkout.checkoutCart.cart.CartLines;

    const plantPostCodeInfoObject: IProductDeliveryInfo | undefined = {
        plantPostCodeInfo: {
            postCodeValidation: {
                invalidAddressForCartProducts: checkoutModulesSharedState?.invalidAddressForCartProducts,
                notBtPostCode: checkoutModulesSharedState?.notBtPostCode
            },
            cartLines: cartLines,
            plantProductsIds: plantProducts,
            postCodeErrorMsg: postCodeErrorMsgForCartLines
        }
    };

    const { isAuthenticated } = props.context.request.user;
    /* VSI Customization - END - 30/11/20 */
    return (
        <Module {...checkoutProps}>
            {!hasSalesOrder && <Node {...headerProps}>{title}</Node>}
            {!hasSalesOrder && (
                <Node {...bodyProps}>
                    {loading}
                    {alert}
                    {canShow && (
                        <>
                            <Node {...mainProps}>
                                {/* VSI Customization - START - 18/11/20 */}
                                <GuidedFormComponent
                                    {...{
                                        items: checkoutItems,
                                        moduleState: props.moduleState,
                                        disableGuidedCheckoutFlow: props.disableGuidedCheckoutFlow,
                                        resource: resources,
                                        isMobile: props.isMobile,
                                        isEditor: false,
                                        rootLink: breadCrumbsRoot,
                                        breadCrumbsText: breadCrumbs,
                                        invalidAddressForCartProducts: checkoutModulesSharedState?.invalidAddressForCartProducts,
                                        notBtPostCodeForPlants: checkoutModulesSharedState?.notBtPostCode,
                                        isFirstAddress: checkoutModulesSharedState?.isFirstAddress,
                                        loggedInUserNewAddress: checkoutModulesSharedState?.loggedInUserNewAddress,
                                        postCodeRestrictionMsg: postCodeErrorMsgForShippingAddress
                                    }}
                                />
                                {/* VSI Customization - END */}
                            </Node>
                            <Node {...sideProps}>
                                {orderSummary && <OrderSummaryComponent {...orderSummary} />}
                                {/* {orderSummary && _renderOrderSummarylines(orderSummary, props)} */}
                                <div className='checkout-need-help'>
                                    <h3>Do you need help with this order?</h3>
                                    <p>You can contact <a href='/contact-us'>Customer Service here</a></p>
                                </div>
                                {!isAuthenticated && creditBannerSection && (
                                    <Node className='credit-availability-banner' tag='div'>
                                        {creditBannerSection}
                                    </Node>
                                )}
                                {lineItems && <LineItemsComponent {...lineItems} {...plantPostCodeInfoObject} />}
                            </Node>
                        </>
                    )}
                </Node>
            )}
            {placeOrderButton}
            {hasSalesOrder && orderConfirmation}
        </Module>
    );
};

export default CheckoutView;
